import React from 'react';
import BackgroundCover from '../../components/backgroundCover/BackgroundCover';
import { useLanguage } from '../../context/LanguageContext';
import { imprintTemplateTranslations, ImprintTranslations } from '../../translations/imprintTemplate';
import { useEditMode } from '../../context/EditModeContext';
import EditableText from '../../components/EditableText/EditableText';
import { usePageContent } from '../../hooks/usePageContent';

function Imprint() {
    const { language } = useLanguage();
    const { isEditMode } = useEditMode();
    const { content, loading } = usePageContent<ImprintTranslations>('imprint');
    
    // Use translations as fallback content
    const t = content || imprintTemplateTranslations[language];

    // Show loading state if needed
    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin h-12 w-12 border-2 border-gray-400 rounded-full border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="">
            <BackgroundCover
                backgroundHeight={600}
                imagePosition="initial"
                backgroundImage="/images/clockwork_light_background.png"
            >
                <div className="absolute top-1/2 left-[10%] -translate-y-1/2 z-10 hidden md:block">
                    <h1 className="text-4xl font-bold text-white">
                        {isEditMode ? (
                            <EditableText
                                contentKey="imprint.pageTitle"
                                defaultValue={t.pageTitle}
                            />
                        ) : t.pageTitle}
                    </h1>
                </div>
            </BackgroundCover>

            <div className="mx-auto px-4 md:px-0 py-12 md:py-24">
                <div className="block md:hidden mb-8">
                    <h1 className="text-4xl font-bold">
                        {isEditMode ? (
                            <EditableText
                                contentKey="imprint.pageTitle"
                                defaultValue={t.pageTitle}
                            />
                        ) : t.pageTitle}
                    </h1>
                </div>

                <div className="flex flex-col items-center">
                    <div className="max-w-4xl">
                        <section className="mb-8">
                            <p className="font-semibold">Realization Desal AG</p>
                            <p>Steinibachstrasse 3</p>
                            <p>6317 Oberwil b. Zug</p>
                            <p>{language === 'de' ? 'Schweiz' : 'Switzerland'}</p>
                        </section>

                        <section className="mb-8 space-y-2">
                            <p><span className="font-semibold">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.phone"
                                        defaultValue={t.phone}
                                    />
                                ) : t.phone}
                            </span>: +41 415252533</p>
                            <p><span className="font-semibold">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.email"
                                        defaultValue={t.email}
                                    />
                                ) : t.email}
                            </span>: contact@niveau-eleve.ch</p>
                        </section>

                        <section className="mb-12">
                            <h2 className="text-2xl font-bold mb-4">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.authorizedRepresentatives"
                                        defaultValue={t.authorizedRepresentatives}
                                    />
                                ) : t.authorizedRepresentatives}
                            </h2>
                            <p><span className="font-semibold">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.boardOfDirectors"
                                        defaultValue={t.boardOfDirectors}
                                    />
                                ) : t.boardOfDirectors}
                            </span>: Michael Bonke</p>
                        </section>

                        <section className="mb-12 space-y-2">
                            <h2 className="text-2xl font-bold mb-4">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.registerEntry"
                                        defaultValue={t.registerEntry}
                                    />
                                ) : t.registerEntry}
                            </h2>
                            <p><span className="font-semibold">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.registeredCompanyName"
                                        defaultValue={t.registeredCompanyName}
                                    />
                                ) : t.registeredCompanyName}
                            </span>: Realization Desal AG</p>
                            <p><span className="font-semibold">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.companyNo"
                                        defaultValue={t.companyNo}
                                    />
                                ) : t.companyNo}
                            </span>: CHE-406.044.624</p>
                            <p><span className="font-semibold">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.vatNumber"
                                        defaultValue={t.vatNumber}
                                    />
                                ) : t.vatNumber}
                            </span>: CHE-406.044.624 MWST</p>
                            <p><span className="font-semibold">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.registerNumber"
                                        defaultValue={t.registerNumber}
                                    />
                                ) : t.registerNumber}
                            </span>: CH-170.3.040.454-4</p>
                        </section>

                        <section className="mb-12">
                            <h2 className="text-2xl font-bold mb-4">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.disclaimer"
                                        defaultValue={t.disclaimer}
                                    />
                                ) : t.disclaimer}
                            </h2>
                            <div className="text text-gray-600 leading-relaxed space-y-4">
                                {isEditMode ? (
                                    t.disclaimerText.map((text, index) => (
                                        <EditableText
                                            key={index}
                                            contentKey={`imprint.disclaimerText.${index}`}
                                            defaultValue={text}
                                            multiline={true}
                                        />
                                    ))
                                ) : (
                                    t.disclaimerText.map((text, index) => (
                                        <p key={index}>{text}</p>
                                    ))
                                )}
                            </div>
                        </section>

                        <section className="mb-12">
                            <h2 className="text-2xl font-bold mb-4">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.disclaimerForLinks"
                                        defaultValue={t.disclaimerForLinks}
                                    />
                                ) : t.disclaimerForLinks}
                            </h2>
                            <p className="text-gray-600 leading-relaxed">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.disclaimerForLinksText"
                                        defaultValue={t.disclaimerForLinksText}
                                        multiline={true}
                                    />
                                ) : t.disclaimerForLinksText}
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.copyright"
                                        defaultValue={t.copyright}
                                    />
                                ) : t.copyright}
                            </h2>
                            <p className="text-gray-600 leading-relaxed">
                                {isEditMode ? (
                                    <EditableText
                                        contentKey="imprint.copyrightText"
                                        defaultValue={t.copyrightText}
                                        multiline={true}
                                    />
                                ) : t.copyrightText}
                            </p>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Imprint; 